import React, { useState, useEffect } from 'react';
import {
  Building,
  BookOpen,
  Menu,
  X,
  Settings,
  ShoppingBag,
  ShoppingCart,
  RefreshCw,
  Zap,
  Brain,
  Receipt,
  FileText,
  BarChart3,
  TrendingUp,
  LogOut,
  User,
  Shield,
  UserCheck,
  CreditCard,
  AlertCircle
} from 'lucide-react';

// Import authentication components
import Login from './components/Login';
import PrivateRoute from './components/PrivateRoute';

// Import existing components
import ZohoBooksOverview from './components/ZohoBooksOverview';

// Import new components
import RecurringInvoicesIntegrationManager from './components/RecurringInvoicesIntegrationManager';
import RecurringExpensesIntegrationManager from './components/RecurringExpensesIntegrationManager';

// Import new dashboard component
import PurchaseBillsIntegrationManager from './components/PurchaseBillsIntegrationManager';
import InvoiceManagement from './components/InvoiceManagement';
import VendorCreditManagement from './components/VendorCreditManagement';
import CreditNoteManagement from './components/CreditNoteManagement';
import DailySpendZohoIntegration from './components/DailySpendZohoIntegration';
import CompleteExpenseManager from './components/CompleteExpenseManager';
import EnhancedReimbursementManager from './components/EnhancedReimbursementManager';

// Import the Complete Approver Dashboard
import ApproverDashboard from './components/ApproverDashboard';

// Import the Complete Employee Dashboard
import EmployeeDashboard from './components/EmployeeDashboard';

import EmployeeProfile from './components/EmployeeProfile';

import SuperAdminDashboard from './components/SuperAdminDashboard';

import PaymentManagement from './components/PaymentManagement';

const App = () => {
  // Authentication state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [currentCompany, setCurrentCompany] = useState(null);
  const [isInitializing, setIsInitializing] = useState(true);

  // Zoho Books organization state
  const [currentZohoOrganization, setCurrentZohoOrganization] = useState(null);
  const [tokenStatus, setTokenStatus] = useState({
    has_token: false,
    expires_in_minutes: 0,
    token_valid: false
  });

  // Existing app state
  const [activeTab, setActiveTab] = useState('overview');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const API_BASE_URL = 'https://api.zapbooksai.subraps.com/api';

  // Get navigation based on user role
  const getNavigationForRole = (userRole) => {
    const navigationConfig = {
      super_admin: [
        {
          id: 'super-admin-dashboard',
          name: 'Super Admin ✍️📝✅🗑️',
          icon: Shield,
          component: SuperAdminDashboard,
          category: 'Administration',
          featured: true
        }
      ],
      admin: [
        {
          id: 'overview',
          name: 'Books Overview 🧾',
          icon: Building,
          component: ZohoBooksOverview,
          category: 'Dashboard'
        },
        {
          id: 'daily-spends',
          name: 'Daily Spend 💸',
          icon: Brain,
          component: DailySpendZohoIntegration,
          category: 'Integration',
          featured: true
        },
        {
          id: 'expense-integration',
          name: 'Expenses 🧾',
          icon: Receipt,
          component: CompleteExpenseManager,
          category: 'Integration',
          featured: true
        },
        {
          id: 'reimbursement-manager',
          name: 'Reimbursement 💼',
          icon: FileText,
          component: EnhancedReimbursementManager,
          category: 'Integration',
          featured: true
        },
        {
          id: 'bills-integration',
          name: 'Purchase 🛒',
          icon: ShoppingBag,
          component: PurchaseBillsIntegrationManager,
          category: 'Integration',
          featured: true
        },
        {
          id: 'vendor-credits-integration',
          name: 'Vendor Debit Note 📉',
          icon: FileText,
          component: VendorCreditManagement,
          category: 'Integration',
          featured: true
        },
        {
          id: 'invoice-integration',
          name: 'Sales 📈',
          icon: TrendingUp,
          component: InvoiceManagement,
          category: 'Integration',
          featured: true
        },
        {
          id: 'customer-credit-note',
          name: 'Customer Credit Note 📄',
          icon: FileText,
          component: CreditNoteManagement,
          category: 'Integration',
          featured: true
        },
        {
          id: 'payment-management',
          name: 'Receipt 📄🔄💳💵',
          icon: CreditCard,
          component: PaymentManagement,
          category: 'Integration',
          featured: true
        },
        {
          id: 'recurring-expenses-integration',
          name: 'Recurring Expenses 🔄💳',
          icon: RefreshCw,
          component: RecurringExpensesIntegrationManager,
          category: 'Integration',
          featured: true
        },
        {
          id: 'recurring-invoice-integration',
          name: 'Recurring Invoices 🔁🧾',
          icon: RefreshCw,
          component: RecurringInvoicesIntegrationManager,
          category: 'Integration',
          featured: true
        }
      ],
      approver: [
        {
          id: 'approver-dashboard',
          name: 'Approval Center 🔁🧾💸',
          icon: UserCheck,
          component: ApproverDashboard,
          category: 'Dashboard',
          featured: true
        }
      ],
      employee: [
        {
          id: 'employee-dashboard',
          name: 'Dashboard 🧾🔄💳💵',
          icon: User,
          component: EmployeeDashboard,
          category: 'Dashboard'
        },
        {
          id: 'my-profile',
          name: 'My Profile 💼',
          icon: User,
          component: EmployeeProfile,
          category: 'Settings'
        }
      ]
    };

    return navigationConfig[userRole] || navigationConfig.employee;
  };

  // Get navigation based on user role
  const navigation = React.useMemo(() => {
    if (!user) return [];
    const userRole = user.loginMode || user.role || 'employee';
    return getNavigationForRole(userRole);
  }, [user]);

  // Update default active tab based on role
  React.useEffect(() => {
    if (user && navigation.length > 0) {
      const userRole = user.loginMode || user.role;
      let defaultTab = 'overview';

      switch (userRole) {
        case 'super_admin':
          defaultTab = 'super-admin-dashboard';
          break;
        case 'admin':
          defaultTab = 'overview';
          break;
        case 'approver':
          defaultTab = 'approver-dashboard';
          break;
        case 'employee':
          defaultTab = 'employee-dashboard';
          break;
        default:
          defaultTab = navigation[0]?.id || 'overview';
      }

      setActiveTab(defaultTab);
    }
  }, [user, navigation]);

  // Check authentication status on component mount
  useEffect(() => {
    checkAuthStatus();
  }, []);

  // Fetch current Zoho organization when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchCurrentZohoOrganization();
      const interval = setInterval(fetchCurrentZohoOrganization, 30000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated]);

  // Function to fetch current Zoho organization
  const fetchCurrentZohoOrganization = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/token/status`);
      const result = await response.json();

      if (result.success) {
        setCurrentZohoOrganization(result.data.current_organization);
        setTokenStatus(result.data);
      }
    } catch (err) {
      console.error('Error fetching current Zoho organization:', err);
    }
  };

  const checkAuthStatus = () => {
    try {
      const token = localStorage.getItem('authToken');
      const userData = localStorage.getItem('userData');
      const userCompanies = localStorage.getItem('userCompanies');

      if (token && userData && userCompanies) {
        const parsedUser = JSON.parse(userData);
        const parsedCompanies = JSON.parse(userCompanies);

        setUser(parsedUser);
        setCompanies(parsedCompanies);
        setIsAuthenticated(true);

        const defaultCompany = parsedCompanies.find(comp => comp.is_default === 1) || parsedCompanies[0];
        if (defaultCompany) {
          setCurrentCompany(defaultCompany);
        }
      }
    } catch (error) {
      console.error('Error checking auth status:', error);
      handleLogout();
    } finally {
      setIsInitializing(false);
    }
  };

  const handleLogin = (authData) => {
    setUser(authData.user);
    setCompanies(authData.companies);
    setIsAuthenticated(true);

    const defaultCompany = authData.companies.find(comp => comp.is_default === 1) || authData.companies[0];
    if (defaultCompany) {
      setCurrentCompany(defaultCompany);
    }
  };

  const handleLogoutWithConfirmation = () => {
    if (window.confirm('Are you sure you want to log out?')) {
      handleLogout();
    }
  };

  const handleLogout = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (token) {
        await fetch('https://api.zapbooksai.subraps.com/api/auth/logout', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('authToken');
      localStorage.removeItem('userData');
      localStorage.removeItem('userCompanies');

      setIsAuthenticated(false);
      setUser(null);
      setCompanies([]);
      setCurrentCompany(null);
      setCurrentZohoOrganization(null);
      setActiveTab('overview');
    }
  };

  const handleOrganizationSwitch = (newOrganization, newTokenStatus) => {
    setCurrentZohoOrganization(newOrganization);
    if (newTokenStatus) {
      setTokenStatus(newTokenStatus);
    }
  };

  // Group navigation by category
  const groupedNavigation = navigation.reduce((acc, item) => {
    if (!acc[item.category]) {
      acc[item.category] = [];
    }
    acc[item.category].push(item);
    return acc;
  }, {});

  const activeComponent = navigation.find(nav => nav.id === activeTab)?.component;

  const getComponentProps = () => {
    if (activeTab === 'overview') {
      return {
        onOrganizationSwitch: handleOrganizationSwitch
      };
    }

    if (activeTab === 'super-admin-dashboard') {
      return {
        user: user
      };
    }

    if (activeTab === 'approver-dashboard') {
      return {
        user: user,
        currentOrganization: currentZohoOrganization
      };
    }

    if (activeTab === 'employee-dashboard') {
      return {
        user: user,
        currentOrganization: currentZohoOrganization
      };
    }
    if (activeTab === 'my-profile') {
      return {
        user: user,
        currentOrganization: currentZohoOrganization
      };
    }

    if (activeTab === 'customer-integration' ||
      activeTab === 'expense-integration' ||
      activeTab === 'bills-integration' ||
      activeTab === 'vendor-creation' ||
      activeTab === 'vendor-integration' ||
      activeTab === 'recurring-expenses-integration' ||
      activeTab === 'recurring-bills-integration' ||
      activeTab === 'recurring-invoice-integration' ||
      activeTab === 'vendor-credits-integration' ||
      activeTab === 'daily-spends' ||
      activeTab === 'invoice-integration' ||
      activeTab === 'reimbursement-integration' ||
      activeTab === 'reimbursement-manager' ||
      activeTab === 'payment-management') {
      return {
        currentOrganization: currentZohoOrganization,
        tokenStatus: tokenStatus
      };
    }

    return {};
  };

  const ActiveComponent = activeComponent || ZohoBooksOverview;
  const componentProps = getComponentProps();

  const categoryColors = {
    'Administration': 'text-purple-600',
    'Dashboard': 'text-blue-600',
    'Accounting': 'text-green-600',
    'Custom': 'text-purple-600',
    'Integration': 'text-teal-600',
    'Purchases': 'text-orange-600',
    'Masters': 'text-indigo-600',
    'Approvals': 'text-amber-600',
    'Reports': 'text-emerald-600',
    'My Data': 'text-pink-600',
    'Settings': 'text-gray-600'
  };

  const categoryIcons = {
    'Administration': Shield,
    'Dashboard': Building,
    'Accounting': BookOpen,
    'Custom': Settings,
    'Integration': Zap,
    'Purchases': ShoppingBag,
    'Masters': ShoppingCart,
    'Approvals': UserCheck,
    'Reports': BarChart3,
    'My Data': User,
    'Settings': Settings
  };

  function getPageDescription(tabId) {
    const descriptions = {
      'super-admin-dashboard': 'Complete system administration and user management dashboard',
      'overview': 'Overview of all your Zoho Books data with advanced management',
      'approver-dashboard': 'Complete approval center for all company transactions',
      'employee-dashboard': 'Your personal expense dashboard and quick actions',
      'pending-approvals': 'Review and approve submitted expenses and transactions',
      'approved-transactions': 'View history of approved expenses and transactions',
      'expense-reports': 'Detailed expense reports for your approval scope',
      'team-analytics': 'Analytics and insights for your team expenses',
      'my-expenses': 'View and manage your submitted expenses',
      'my-reimbursements': 'Track your reimbursement requests and status',
      'expense-tracker': 'Personal expense tracking and analytics',
      'my-profile': 'Manage your profile and settings',
      'daily-spends': 'Complete expense integration with AI categorization',
      'expense-integration': 'Complete expense integration with AI categorization',
      'bills-integration': 'Complete bills integration with AI categorization',
      'payment-management': 'Complete payment integration with AI categorization'
    };

    return descriptions[tabId] || 'Manage your business operations efficiently';
  }

  // Get role-based configuration
  const getRoleConfig = () => {
    const userRole = user?.loginMode || user?.role || 'employee';
    const roleConfigs = {
      super_admin: {
        title: 'Super Admin Dashboard',
        description: 'Complete system administration and user management',
        color: 'text-purple-600',
        bgColor: 'bg-purple-50',
        panelText: 'SUPER ADMIN PANEL'
      },
      admin: {
        title: 'Admin Dashboard',
        description: 'Full system access and management',
        color: 'text-teal-600',
        bgColor: 'bg-teal-50',
        panelText: 'ADMIN PANEL'
      },
      approver: {
        title: 'Approver Dashboard',
        description: 'Review and approve team transactions',
        color: 'text-blue-600',
        bgColor: 'bg-blue-50',
        panelText: 'APPROVER PANEL'
      },
      employee: {
        title: 'Employee Dashboard',
        description: 'Manage your personal expenses',
        color: 'text-green-600',
        bgColor: 'bg-green-50',
        panelText: 'EMPLOYEE PANEL'
      }
    };

    return roleConfigs[userRole] || roleConfigs.employee;
  };

  const roleConfig = getRoleConfig();

  // Show loading screen during initialization
  if (isInitializing) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-white rounded-full shadow-lg mb-4">
            <RefreshCw className="w-8 h-8 text-teal-600 animate-spin" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Loading Business Hub</h2>
          <p className="text-gray-600">Initializing your accounting dashboard...</p>
        </div>
      </div>
    );
  }

  // Show login screen if not authenticated
  if (!isAuthenticated) {
    return <Login onLogin={handleLogin} />;
  }

  return (
    <PrivateRoute>
      <div className="min-h-screen bg-gray-50 flex">
        {/* Mobile sidebar backdrop */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Sidebar */}
        <div className={`
          fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out
          lg:translate-x-0 lg:static lg:inset-0
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}>
          {/* Sidebar Header with Role */}
          <div className="flex items-center justify-between h-16 px-6 border-b border-gray-200">
            <div className="flex items-center gap-2">
              <Zap className="w-8 h-8 text-teal-600" />
              <div>
                <h1 className="text-lg font-bold text-gray-900">Business Hub</h1>
                <p className={`text-xs ${roleConfig.color} font-medium`}>
                  {roleConfig.panelText}
                </p>
              </div>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden text-gray-400 hover:text-gray-600"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <nav className="flex-1 overflow-y-auto py-4">
            {Object.entries(groupedNavigation).map(([category, items]) => {
              const CategoryIcon = categoryIcons[category];
              return (
                <div key={category} className="mb-6">
                  <div className="px-6 mb-2">
                    <div className={`flex items-center gap-2 text-xs font-semibold uppercase tracking-wider ${categoryColors[category]}`}>
                      <CategoryIcon className="w-4 h-4" />
                      {category}
                    </div>
                  </div>
                  <div className="px-3 space-y-1">
                    {items.map((item) => {
                      const IconComponent = item.icon;
                      const isActive = activeTab === item.id;

                      return (
                        <button
                          key={item.id}
                          onClick={() => {
                            setActiveTab(item.id);
                            setSidebarOpen(false);
                          }}
                          className={`
                            w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors relative
                            ${isActive
                              ? 'bg-teal-100 text-teal-700 border-r-2 border-teal-700'
                              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                            }
                            ${item.featured ? 'ring-2 ring-teal-200 bg-teal-50' : ''}
                          `}
                        >
                          <IconComponent className="w-5 h-5 flex-shrink-0" />
                          <span className="truncate">{item.name}</span>
                          {item.featured && (
                            <span className="absolute -top-1 -right-1 w-2 h-2 bg-teal-500 rounded-full"></span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </nav>

          {/* Footer with role info */}
          <div className="border-t border-gray-200 p-4">
            <button
              onClick={handleLogoutWithConfirmation}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors"
            >
              <LogOut className="w-4 h-4" />
              Sign Out
            </button>

            <div className="text-xs text-gray-500 text-center mt-3">
              <p className={`font-medium ${roleConfig.color}`}>
                {roleConfig.description}
              </p>
              <p className="mt-1 text-teal-600">Secure • Role-Based Access</p>
            </div>
          </div>
        </div>

        {/* Main content */}
        <div className="flex-1 flex flex-col lg:ml-0">
          {/* Top bar */}
          <div className="bg-white shadow-sm border-b border-gray-200 px-4 py-3 flex items-center justify-between lg:px-6">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden text-gray-400 hover:text-gray-600"
              >
                <Menu className="w-6 h-6" />
              </button>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  {navigation.find(nav => nav.id === activeTab)?.name || roleConfig.title}
                </h2>
                <p className="text-sm text-gray-600">
                  {getPageDescription(activeTab)}
                </p>
              </div>
            </div>

            {/* Role-specific status indicator */}
            <div className="flex items-center gap-4">
              {/* User Role Badge */}
              <div className={`px-3 py-1 rounded-full text-xs font-medium ${roleConfig.bgColor} ${roleConfig.color}`}>
                {(user?.loginMode || user?.role || 'employee').toUpperCase()}
              </div>

              {/* User Info */}
              <div className="hidden md:flex items-center gap-2">
                <div className="text-right">
                  <p className="text-sm font-medium text-gray-900">{user?.fullName || user?.username}</p>
                  <p className="text-xs text-gray-500">
                    {user?.companyName || currentZohoOrganization?.name || currentCompany?.company_name || 'No Organization'}
                  </p>
                </div>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${roleConfig.bgColor}`}>
                  <User className={`w-4 h-4 ${roleConfig.color}`} />
                </div>
              </div>

              {/* Connection Status */}
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-sm text-gray-600 hidden sm:inline">Connected</span>
              </div>
            </div>
          </div>

          {/* Page content */}
          <main className="flex-1 overflow-auto">
            <div className="p-4 lg:p-6">
              {/* Show organization selector for customer integration if no organization */}
              {(activeTab === 'customer-integration' && !currentZohoOrganization) ? (
                <div className="space-y-6">
                  <ZohoBooksOverview onOrganizationSwitch={handleOrganizationSwitch} />
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <div className="flex items-center">
                      <AlertCircle className="w-5 h-5 text-yellow-500 mr-2" />
                      <div>
                        <h3 className="text-yellow-800 font-medium">Organization Required</h3>
                        <p className="text-yellow-700 text-sm mt-1">
                          Please select a ZohoBooks organization above to view customers.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <ActiveComponent {...componentProps} />
              )}
            </div>
          </main>
        </div>
      </div>
    </PrivateRoute>
  );
};

export default App;